import React, { useEffect, useMemo, useState } from 'react'

// types
import {
  EcommerceResponse,
  assetsCollectionElement,
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema
} from '@aytm/app/swidget/base/questions/Ecommerce/types'

// functions
import { formatPriceWithLocale } from '../../functions'
import { stripHighlightTags } from '../../utils/stripHighlightTags'

// components
import Rating from './Rating'
import AddToCartButton from './AddToCartButton'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import AddedToCartModal from './AddedToCartModal'

// custom hooks
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import MultipleCarousel from './MultipleCarousel'
import { useChildrenProducts } from '../../hooks/useChildrenProducts'
import ProductPreview from './ProductPreview'

// styles
import style from './Product.module.scss'
import commonStyle from '../Costco.module.scss'
import AssetsBlock from './AssetsBlock'
import { unescapeTags } from '@aytm/lib/common/func'

const SAVINGS_STARTED_DAYS_AGO = 21
const SAVINGS_ENDS_IN_DAYS = 5
const DAY = 24 * 60 * 60 * 1000

interface IProductProps {
  answer: IEcommerceAnswerSchema
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  index: number
  findability: boolean
  onItemClose: () => void
  onCartOpen: () => void
  onItemSelect: (index: number) => void
  onAddToCart: (qnt: number, index: number) => void
  scrollTop: () => void
}

const Product = ({
  answer,
  schema,
  response,
  index,
  findability,
  onItemClose,
  onCartOpen,
  onItemSelect,
  onAddToCart,
  scrollTop
}: IProductProps) => {
  // custom hooks calls
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()
  const childrenProducts = useChildrenProducts({ answer, schema, excludeCurrentProduct: true })

  const assetsCollection: assetsCollectionElement[] = useMemo(
    () => [
      ...(answer.image ? [answer.image] : []),
      ...(answer.gallery || []),
      ...(answer.view3d ? [answer.view3d] : [])
    ],
    [answer]
  )

  // state
  const [showModal, setShowModal] = useState<boolean>(false)
  const [quantityError, setQuantityError] = useState<boolean>(false)
  const [amount, setAmount] = useState<number>(1)

  // variables
  const discounted = Boolean(answer.discounted_price) && answer.price > answer.discounted_price
  const price = (value: number, noPoint = false) =>
    formatPriceWithLocale(value, schema.price_mask, languageTag, true, noPoint)

  const savingsPeriod = useMemo(() => {
    const format = (date: Date) =>
      new Intl.DateTimeFormat(languageTag, { month: 'numeric', day: 'numeric', year: '2-digit' }).format(date)
    const now = Date.now()
    return {
      from: format(new Date(now - SAVINGS_STARTED_DAYS_AGO * DAY)),
      to: format(new Date(now + SAVINGS_ENDS_IN_DAYS * DAY))
    }
  }, [languageTag])

  // functions
  const onAdjust = (qnt: -1 | 1) => {
    if (1 === amount && -1 === qnt) {
      setQuantityError(true)
    } else {
      setQuantityError(false)
      setAmount(value => value + qnt)
    }
  }

  const onMobileAddToCart = (qnt: number, productIndex: number) => {
    const firstAdd = qnt > 0 && 0 === response.answers[productIndex].v
    onAddToCart(qnt, productIndex)
    if (firstAdd && !findability) {
      setShowModal(true)
    }
  }

  const renderOnlinePrice = () => (
    <div className={style.onlinePrice}>
      <span>{t('questions', 'ec', 'costco', 'onlinePrice')}</span>
      <EmptyLink color="#0060A9">
        <div className={style.ask}>i</div>
      </EmptyLink>
    </div>
  )

  const renderSavings = () => (
    <>
      <span dangerouslySetInnerHTML={{ __html: price(answer.price - answer.discounted_price) }} />
      &nbsp;
      {t('questions', 'ec', 'costco', 'off')}
    </>
  )

  // effects
  useEffect(() => {
    scrollTop()
  }, [scrollTop])

  // render
  return (
    <div
      className={`${style.productContainer} ${viewport.mobile ? style.productContainerMobile : ''}`}
      data-testid="SW-EC-PDP"
    >
      <button className={style.back} onClick={onItemClose}>
        <i className="pi pi-chevron-left" />
        <span>{t('questions', 'ec', 'costco', 'back')}</span>
      </button>
      {showModal && (
        <AddedToCartModal
          answer={answer}
          schema={schema}
          index={index}
          amount={amount}
          findability={findability}
          quantity={response.answers[index].v}
          onItemClose={onItemClose}
          onCartOpen={onCartOpen}
          onItemSelect={onItemSelect}
          onAddToCart={onAddToCart}
          onModalClose={() => setShowModal(false)}
        />
      )}
      <section className={style.product}>
        {assetsCollection.length > 0 ? (
          <AssetsBlock key={answer.id} answer={answer} assetsCollection={assetsCollection} />
        ) : (
          <div className={style.noImage} />
        )}
        <div className={style.info}>
          <div className={style.card}>
            <div className={style.cardTop}>
              <div className={style.labels}>
                {answer.red_label && (
                  <div className={commonStyle.purpleLabel} dangerouslySetInnerHTML={{ __html: answer.red_label }} />
                )}
                {answer.green_label && (
                  <div
                    className={commonStyle.blueLabel}
                    dangerouslySetInnerHTML={{ __html: stripHighlightTags(answer.green_label) }}
                  />
                )}
              </div>
            </div>
            {answer.sub_title && (
              <div className={style.brand}>
                <EmptyLink color="#0060A9">
                  <span className={style.brandText} dangerouslySetInnerHTML={{ __html: answer.sub_title }} />
                </EmptyLink>
                {!viewport.mobile && (
                  <EmptyLink color="#0060A9">
                    <i className={`${commonStyle.costcoIcon} ${commonStyle.share}`} />
                  </EmptyLink>
                )}
              </div>
            )}
            <div className={style.caption} dangerouslySetInnerHTML={{ __html: answer.text }} />
            <div className={style.code}>
              {t<(code: string) => string>('questions', 'ec', 'costco', 'code')(`187893${index}`)}
            </div>
            <div className={style.ratingWrapper}>
              <Rating rating={answer.rating} />
              <div className={style.rating}>
                {answer.rating}&nbsp;({answer.reviews})
              </div>
              {viewport.mobile ? (
                <div className={style.ratingShare}>
                  <EmptyLink color="#0060A9">
                    <i className={`${commonStyle.costcoIcon} ${commonStyle.share}`} />
                  </EmptyLink>
                </div>
              ) : (
                <EmptyLink color="#0060A9">
                  <div className={style.writeReview}>{t('questions', 'ec', 'costco', 'writeReview')}</div>
                </EmptyLink>
              )}
            </div>
            {1 === answer.prime && (
              <div className={style.memberItem}>
                <div className={commonStyle.memberOnly}>{t('questions', 'ec', 'costco', 'memberItem')}</div>
              </div>
            )}
          </div>
          <div className={style.card}>
            <div className={style.deliveryTitle}>{t('questions', 'ec', 'costco', 'twoDayDelivery')}</div>
            {viewport.mobile && renderOnlinePrice()}
            <div className={style.priceRow}>
              <div
                className={style.priceMain}
                dangerouslySetInnerHTML={{
                  __html: price(discounted ? answer.discounted_price : answer.price, true)
                }}
              />
              {discounted && (
                <div className={style.priceAside}>
                  <div className={style.off}>{renderSavings()}</div>
                  <div className={style.was}>
                    {t('questions', 'ec', 'costco', 'was')}&nbsp;
                    <span dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
                  </div>
                </div>
              )}
            </div>
            {answer.per_count_price && (
              <div
                className={style.perCount}
                dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }}
              />
            )}
            {discounted && (
              <>
                <div className={style.off}>{renderSavings()}</div>
                <div className={style.savingsNote}>
                  <span dangerouslySetInnerHTML={{ __html: price(answer.price - answer.discounted_price) }} />
                  &nbsp;
                  {t<(from: string, to: string) => string>(
                    'questions',
                    'ec',
                    'costco',
                    'savingsValid'
                  )(savingsPeriod.from, savingsPeriod.to)}
                </div>
                <EmptyLink color="#0060A9">
                  <div className={style.link}>{t('questions', 'ec', 'costco', 'termsAndConditions')}</div>
                </EmptyLink>
              </>
            )}
            {!viewport.mobile && renderOnlinePrice()}
          </div>
          <div className={style.card}>
            <div className={style.cardTitle}>{t('questions', 'ec', 'costco', 'howToGetIt')}</div>
            <div className={style.getIt}>
              <div className={style.getItColumn}>
                <div className={style.getItHead}>
                  <i className="pi pi-box" />
                  <EmptyLink color="#0060A9">
                    <span className={style.getItLink}>{t('questions', 'ec', 'costco', 'myIndex')}</span>
                  </EmptyLink>
                </div>
                <div className={style.green}>{t('questions', 'ec', 'costco', 'available')}</div>
              </div>
              <div className={style.getItColumn}>
                <div className={style.getItHead}>
                  <i className="pi pi-building" />
                  <EmptyLink color="#0060A9">
                    <span className={style.getItLink}>{t('questions', 'ec', 'costco', 'myCity')}</span>
                  </EmptyLink>
                </div>
                <div>
                  <span className={style.green}>{t('questions', 'ec', 'costco', 'inStock')}</span>&nbsp;
                  {t('questions', 'ec', 'costco', 'buyInWarehouse')}
                </div>
                <div className={style.muted}>{t('questions', 'ec', 'costco', 'warehousePricing')}</div>
              </div>
            </div>
            <div className={style.tiles}>
              <div className={style.tileWrapper}>
                <EmptyLink color="#0060A9">
                  <div className={`${style.tile} ${style.tileActive}`}>
                    <i className={`pi pi-check-circle ${style.tileIcon}`} />
                    <div className={style.tileTitle}>{t('questions', 'ec', 'costco', 'twoDayDelivery')}</div>
                  </div>
                </EmptyLink>
              </div>
              <div className={style.tileWrapper}>
                <EmptyLink color="#0060A9">
                  <div className={style.tile}>
                    <i className={`pi pi-shopping-bag ${style.tileIcon}`} />
                    <div className={style.tileTitle}>{t('questions', 'ec', 'costco', 'sameDay')}</div>
                    <div className={style.tileNote}>{t('questions', 'ec', 'costco', 'pricingMayVary')}</div>
                  </div>
                </EmptyLink>
              </div>
            </div>
            <div className={style.feeLine}>
              <span>{t('questions', 'ec', 'costco', 'twoDayDelivery')}</span>
              <EmptyLink color="#0060A9">
                <div className={style.ask}>i</div>
              </EmptyLink>
            </div>
            <div className={style.deliveryTerms}>{t('questions', 'ec', 'costco', 'deliveryTerms')}</div>
          </div>
          <div className={style.card}>
            <div className={style.buyRow} data-testid="SW-EC-PDP-AddToCartBlock">
              {viewport.mobile ? (
                <div className={style.mobileBuy}>
                  <AddToCartButton
                    quantity={response.answers[index].v}
                    findability={findability}
                    index={index}
                    onAddToCart={onMobileAddToCart}
                  />
                </div>
              ) : (
                <>
                  {!findability && (
                    <div className={style.spin}>
                      <button className={style.minus} onClick={() => onAdjust(-1)} />
                      <div className={`${style.count} ${quantityError ? style.countError : ''}`}>{amount}</div>
                      <button className={style.plus} onClick={() => onAdjust(1)} />
                    </div>
                  )}
                  <button
                    className={style.addToCart}
                    data-testid="SW-EC-AddToCartButton"
                    disabled={findability && 1 === response.answers[index].v}
                    onClick={() => {
                      onAddToCart(amount, index)
                      if (!findability) {
                        setShowModal(true)
                      }
                    }}
                  >
                    <i className="pi pi-plus" />
                    <span>{t('questions', 'ec', 'costco', 'add')}</span>
                  </button>
                </>
              )}
              <EmptyLink color="#0060A9">
                <div className={style.wish}>
                  <i className="pi pi-heart" />
                </div>
              </EmptyLink>
            </div>
            {quantityError && <div className={style.quantityError}>{t('questions', 'ec', 'costco', 'qtyError')}</div>}
          </div>
        </div>
      </section>
      <div className={style.accordion}>
        <div className={style.accordionRow}>
          <div className={`${style.accordionHead} ${style.accordionHeadStatic}`}>
            <span className={style.accordionTitle}>{t('questions', 'ec', 'costco', 'details')}</span>
            <EmptyLink color="#0060A9">
              <i className={`pi pi-chevron-up ${style.chevron}`} />
            </EmptyLink>
          </div>
          <div className={style.accordionBody}>
            <div
              className={style.text}
              dangerouslySetInnerHTML={{
                __html: `${answer.feature_bullets} ${answer.description}`
                  .replace(/&lt;ul&gt;/g, '<ul>')
                  .replace(/&lt;\/ul&gt;/g, '</ul>')
                  .replace(/&lt;li&gt;/g, '<li>')
                  .replace(/&lt;\/li&gt;/g, '</li>')
              }}
            ></div>
          </div>
        </div>
        {[
          t('questions', 'ec', 'costco', 'spec'),
          t('questions', 'ec', 'costco', 'returns'),
          t('questions', 'ec', 'costco', 'memberReviews')
        ].map(title => (
          <div className={style.accordionRow} key={title}>
            <EmptyLink color="#0060A9">
              <div className={style.accordionHead}>
                <span className={style.accordionTitle}>{title}</span>
                <i className={`pi pi-chevron-down ${style.chevron}`} />
              </div>
            </EmptyLink>
          </div>
        ))}
      </div>
      {childrenProducts.length > 0 && (
        <div className={style.childrenProducts}>
          <div className={style.similar}>{t('questions', 'ec', 'costco', 'similarProducts')}</div>
          <MultipleCarousel slidesPerView={viewport.mobile ? 2 : 5} showNavigation={false}>
            {childrenProducts.map(answer => (
              <ProductPreview
                key={answer.id}
                data-testid="SW-EC-CarouselItem"
                schema={schema}
                quantity={response.answers.find(product => answer.id === product.id)?.v || 0}
                findability={findability}
                answer={answer}
                index={schema.answers.indexOf(answer)}
                disabled={'focus' == schema.mode_type && !answer.focused}
                showButtons={false}
                fit={viewport.mobile ? 2 : 5}
                showSponsoredLabel={false}
                onItemSelect={index => {
                  onItemClose()
                  onItemSelect(index)
                }}
                onAddToCart={onAddToCart}
              />
            ))}
          </MultipleCarousel>
        </div>
      )}
    </div>
  )
}

export default Product
